import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { helpWhy } from '../../help/why';
import { ICreateFestivalBonusGrantRequest } from '../../interfaces/payroll.interface';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: ICreateFestivalBonusGrantRequest) => void;
    saving?: boolean;
}

// Grant create dialog (spec §3): occasion, target month picker (year + month), percent of basic.
// Bound/uniqueness/cap refusals are validated server-side (spec §2.1); this dialog only enforces
// the shape bean validation already requires so a malformed request never leaves the client.
const FestivalBonusFormDialog: React.FC<Props> = ({ open, onClose, onSave, saving = false }) => {
    const now = new Date();
    const [occasion, setOccasion] = useState('');
    const [targetYear, setTargetYear] = useState(now.getFullYear());
    const [targetMonth, setTargetMonth] = useState(now.getMonth() + 1);
    const [percentOfBasic, setPercentOfBasic] = useState<number>(100);

    useEffect(() => {
        if (open) {
            setOccasion('');
            setTargetYear(now.getFullYear());
            setTargetMonth(now.getMonth() + 1);
            setPercentOfBasic(100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Bounds mirror CreateFestivalBonusGrantRequest's bean validation (occasion @Size(max=80),
    // targetYear @Min(2020)/@Max(2100), percentOfBasic @DecimalMin(1)/@DecimalMax(100)) so a
    // malformed request never leaves the client — an empty/NaN year (e.g. the field cleared)
    // disables submit rather than sending a guaranteed 400.
    const isValid = occasion.trim().length > 0 && occasion.trim().length <= 80
        && Number.isFinite(targetYear) && targetYear >= 2020 && targetYear <= 2100
        && percentOfBasic >= 1 && percentOfBasic <= 100;

    const createWhy = saving ? 'Saving is in progress.'
        : occasion.trim().length === 0 ? 'Enter the occasion first.'
        : !(Number.isFinite(targetYear) && targetYear >= 2020 && targetYear <= 2100) ? 'Enter a target year from 2020 to 2100.'
        : !(percentOfBasic >= 1 && percentOfBasic <= 100) ? 'Enter a percent of basic from 1 to 100.' : null;

    const handleSubmit = () => {
        if (!isValid) return;
        onSave({ occasion: occasion.trim(), targetYear, targetMonth, percentOfBasic });
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.festivalForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>New Festival Bonus Grant</DialogTitle>
            <DialogContent data-help-section="payroll.festivalForm.section.details">
                <TextField
                    autoFocus
                    data-help="payroll.festivalForm.field.occasion"
                    margin="dense"
                    label="Occasion"
                    fullWidth
                    value={occasion}
                    onChange={(e) => setOccasion(e.target.value)}
                    inputProps={{ maxLength: 80 }}
                    sx={{ mb: 2, mt: 1 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="festival-bonus-month-label">Target Month</InputLabel>
                    <Select
                        data-help="payroll.festivalForm.field.month" labelId="festival-bonus-month-label"
                        label="Target Month"
                        value={targetMonth}
                        onChange={(e) => setTargetMonth(Number(e.target.value))}
                    >
                        {MONTHS.map((label, idx) => (
                            <MenuItem key={label} value={idx + 1}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    margin="dense"
                    data-help="payroll.festivalForm.field.year" label="Target Year"
                    type="number"
                    fullWidth
                    value={targetYear}
                    onChange={(e) => setTargetYear(Number(e.target.value))}
                    inputProps={{ min: 2020, max: 2100 }}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    data-help="payroll.festivalForm.field.percent" label="Percent of Basic"
                    type="number"
                    fullWidth
                    value={percentOfBasic}
                    onChange={(e) => setPercentOfBasic(Number(e.target.value))}
                    inputProps={{ min: 1, max: 100, step: '0.01' }}
                />
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.festivalForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="payroll.festivalForm.save" {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)} onClick={handleSubmit} variant="contained" disabled={!isValid || saving}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FestivalBonusFormDialog;
